import { apiGet, apiSend, apiUpload } from './client'
import type {
  DialCode, CurrentUser } from './types'

/**
 * The session is a REST resource: POST to create one, GET to read who it
 * belongs to, DELETE to end it.
 */

export function fetchSession(signal?: AbortSignal): Promise<CurrentUser> {
  return apiGet<CurrentUser>('/session', { signal })
}

export function signIn(email: string, password: string): Promise<CurrentUser | null> {
  return apiSend<CurrentUser>('/session', 'POST', { email, password })
}

/**
 * Which ways in this installation offers.
 *
 * Asked before anybody has signed in, and says nothing about who exists -
 * only what the sign-in page should draw, so it never offers a Google button
 * that leads nowhere.
 */
export type SignInMethods = {
  password: boolean
  google: boolean
  /** Shown when somebody is turned away for being on the wrong domain. */
  google_domains: string
  /** Public; used to save a finance recovery code to the person's own Drive. */
  google_client_id?: string | null
}

export function fetchSignInMethods(signal?: AbortSignal): Promise<SignInMethods> {
  return apiGet<SignInMethods>('/sign-in-method', { signal })
}

/**
 * Setting a password when you do not have one yet.
 *
 * Somebody who has only ever signed in with Google is in that position, and
 * the current password is genuinely not asked for because there is none to
 * prove. The server applies the same rule rather than trusting this.
 */
export function setOwnPassword(password: string, currentPassword?: string): Promise<null> {
  return apiSend<null>('/profile/password', 'PUT', {
    password,
    ...(currentPassword === undefined ? {} : { current_password: currentPassword }),
  }) as Promise<null>
}

/**
 * Sign in as somebody else, as a Super Admin. The server checks; this only
 * asks. The session itself changes, so every cached answer belongs to the
 * wrong person afterwards and the caller starts the app afresh.
 */
export function switchToUser(email: string): Promise<CurrentUser> {
  return apiGet<CurrentUser>('/session', { query: { _switch_user: email } })
}

/** Back to your own account. */
export function returnToOwnAccount(): Promise<CurrentUser> {
  return apiGet<CurrentUser>('/session', { query: { _switch_user: '_exit' } })
}

export function signOut(): Promise<null> {
  return apiSend<null>('/session', 'DELETE') as Promise<null>
}

/**
 * Resolves a permission against the grants the API returned, applying the same
 * wildcard rules the server uses.
 *
 * This only decides what to *show*. Every endpoint checks permissions again
 * server-side, so hiding a button here is a courtesy, never a control.
 */
export function can(user: CurrentUser | undefined, permission: string): boolean {
  if (!user) return false

  return user.permissions.some((grant) => {
    if (grant === '*' || grant === permission) return true
    if (!grant.endsWith('.*')) return false
    // Compare including the trailing dot, so "user.*" does not cover
    // "username.read".
    return permission.startsWith(grant.slice(0, -1))
  })
}

/**
 * Your own account.
 *
 * Under /profile rather than /session because the login authenticator owns
 * the /session path and matches on path alone, so a PATCH there never reaches
 * the controller.
 */
export function updateProfile(body: {
  first_name?: string
  last_name?: string
  /** An empty string clears it; omitting it leaves what is on file. */
  phone?: string
  /** The country code that belongs with it, such as "+44". */
  phone_dial_code?: string
  about?: string
}): Promise<CurrentUser | null> {
  return apiSend<CurrentUser>('/profile', 'PATCH', body)
}

/**
 * Changing your own password needs the current one, which is what separates
 * it from an administrative reset. The session survives, so there is no need
 * to sign in again - but every other session of yours ends.
 */
export function changeOwnPassword(currentPassword: string, password: string): Promise<null> {
  return apiSend<null>('/profile/password', 'PUT', {
    current_password: currentPassword,
    password,
  }) as Promise<null>
}

/**
 * Sent as multipart, because an image arrives as a file from every client
 * that will ever send one and base64 would inflate it by a third to travel
 * through JSON. The server detects the type from the bytes, so the filename
 * and the browser's Content-Type carry no authority.
 */
export async function uploadAvatar(file: File): Promise<CurrentUser | null> {
  const body = new FormData()
  body.append('file', file)

  return apiUpload<CurrentUser>('/profile/avatar', body)
}

export function removeAvatar(): Promise<CurrentUser | null> {
  return apiSend<CurrentUser>('/profile/avatar', 'DELETE')
}

/** The country codes a phone number may carry, as the server defines them. */
export function fetchDialCodes(signal?: AbortSignal): Promise<DialCode[]> {
  return apiGet<DialCode[]>('/dial-code', { signal })
}

/**
 * The shortest password the server accepts, mirrored for the forms.
 *
 * One constant rather than a number in each form: four screens each had
 * their own 12, and a rule copied four times is a rule that changes in three.
 * The server's `PlainPassword::MINIMUM_LENGTH` is still what decides.
 */
export const MINIMUM_PASSWORD_LENGTH = 6
